import type { ReactNode } from 'react';

import Button, { type ButtonVariant } from 'components/Button';
import Icon, { type IconColor, type IconName } from 'components/Icon';
import IconButton from 'components/IconButton';
import Img from 'components/Img';
import Link from 'components/Link';
import RichText from 'components/RichText';
import type { ExtractStrict, HTMLString, UnionWith } from 'types';
import { cn } from 'utils/classNames';
import { getTestDataAttrFrom } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export type MessageVariant =
	| 'error'
	| 'information'
	| 'neutral'
	| 'success'
	| 'summary'
	| 'warning';
export type MessageButtonVariant = ExtractStrict<
	ButtonVariant,
	'branded' | 'cta' | 'secondary'
>;

interface BaseProps {
	buttonText?: string;
	buttonVariant?: MessageButtonVariant;
	className?: string;
	heading?: string;
	icon?: UnionWith<IconName, string>;
	link?: string;
	linkDataCy?: string;
	linkIsScreenReaderHidden?: boolean;
	linkText?: string;
	onButtonClick?: () => void;
	onClose?: () => void;
	/** Only used for the summary variant. */
	summarySender?: string;
	variant: MessageVariant;
}

interface WithChildren extends BaseProps {
	children: ReactNode;
	text?: never;
}
interface WithText extends BaseProps {
	children?: never;
	text: UnionWith<HTMLString, string> | undefined;
}

// Would be more correct to also have a union for the summary variant to force
// summarySender, but the added complexity is not worth it for now.
type Props = WithChildren | WithText;

const variantIconColors: Record<MessageVariant, IconColor | null> = {
	error: 'error',
	information: 'information',
	neutral: null,
	success: 'success',
	summary: 'summary',
	warning: 'warning',
};
const variantIcons: Record<MessageVariant, IconName | null> = {
	error: 'diamond-error',
	information: 'circle-info',
	neutral: null,
	success: 'circle-check',
	summary: 'sparkles',
	warning: 'triangle-warning',
};

const iconIsUrl = (icon: UnionWith<IconName, string> | null): icon is string =>
	Boolean(icon?.includes('/'));
const iconIsName = (
	icon: UnionWith<IconName, string> | null,
): icon is IconName => Boolean(icon && !iconIsUrl(icon));

export default function Message({
	buttonText,
	buttonVariant = 'branded',
	children,
	className,
	heading,
	icon: iconProp,
	link,
	linkDataCy,
	linkIsScreenReaderHidden,
	linkText,
	onButtonClick,
	onClose,
	summarySender,
	text,
	variant,
}: Props) {
	const { t } = useI18n();

	const isSummary = variant === 'summary';

	const icon = iconProp || variantIcons[variant];
	const iconColor = variantIconColors[variant];
	const iconSize = isSummary ? 16 : 24;

	const hasButton = !!(onButtonClick && buttonText);
	const hasLink = !!(link && linkText);

	const iconElement = iconIsUrl(icon) ? (
		<Img
			src={icon}
			alt=""
			className={cn('shrink-0', isSummary ? 'size-4' : 'size-6')}
			width={iconSize}
			height={iconSize}
			jpgOptimized={false}
		/>
	) : iconIsName(icon) && iconColor ? (
		<Icon name={icon} className="shrink-0" color={iconColor} size={iconSize} />
	) : null;

	// TODO: should the icons have an alt or be explained in some way for a11y?
	// A screen reader user doesn't know if a message is information or an error.
	return (
		<div
			className={cn(
				'flex items-start gap-4 rounded-xs p-4',
				variant === 'error' && 'bg-error',
				variant === 'information' && 'bg-information',
				variant === 'neutral' && 'bg-subtle',
				variant === 'success' && 'bg-success',
				variant === 'summary' && 'bg-summary',
				variant === 'warning' && 'bg-warning',
				className,
			)}
		>
			{!isSummary && iconElement}
			<div
				className={cn(
					'text-sm',
					variant === 'error' && 'text-error',
					variant === 'information' && 'text-information',
					variant === 'neutral' && 'text-base',
					variant === 'success' && 'text-success',
					variant === 'summary' && 'text-positive',
					variant === 'warning' && 'text-warning',
					// Better icon alignment when it's guaranteed to be only text.
					!heading && !children && text && 'pt-0.5',
				)}
			>
				{heading && <p className="font-bold">{heading}</p>}
				{children}
				{text && (
					<RichText
						className="mt-1 first:mt-0"
						tightSpacing
						inheritStyle
						html={text}
					/>
				)}
				{isSummary && summarySender && (
					<p className="mt-3 flex items-center gap-2 text-xs">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-summaryStrong">
							{iconElement}
						</span>{' '}
						{summarySender}
					</p>
				)}
				{(hasButton || hasLink) && (
					<div className="mt-4 flex items-center gap-4">
						{hasButton && (
							<Button size="sm" variant={buttonVariant} onClick={onButtonClick}>
								{buttonText}
							</Button>
						)}
						{hasLink && (
							<Link
								href={link}
								data-cy={getTestDataAttrFrom(linkDataCy)}
								underline
								isScreenReaderHidden={linkIsScreenReaderHidden}
							>
								{linkText}
							</Link>
						)}
					</div>
				)}
			</div>
			{onClose && (
				<IconButton
					className="ml-auto"
					size="xs"
					icon="close"
					text={t('general_close_message_button_label')}
					onClick={onClose}
				/>
			)}
		</div>
	);
}
Message.displayName = 'Message';
